import React from 'react';
import { ContentTypeFieldResponse } from '../../../Model/responseDtos/ContentTypeFieldResponse';
import { GEAR_CONTENT_TYPE_FIELD_TYPE } from '../../../Model/enums/GearContentTypeFieldType';
import { Field } from '@progress/kendo-react-form';
import { FormInput } from './fields/FormInput';
import { buildFieldValidators, requiredValidator } from './validators/FormValidator';
import { FormNumberInput } from './fields/FormNumberInput';
import { FormBoolRadioGroup } from './fields/FormBoolRadioGroup';
import { FormDatePicker } from './fields/FormDatePicker';
import { FormDropDownList } from './fields/FormDropdown';
import { FormMultiSelect } from './fields/FormMultiSelect';
import { FormMaskedInput } from './fields/FormMaskedInput';
import { FormSearchableInput } from './fields/FormSearchableInput';
import { FormComboBox } from './fields/FormComboBox';

interface FieldMapperProps {
  ctField: ContentTypeFieldResponse;
  fieldTranslation?: string;
  choices?: string[];
  isLoading?: boolean;
  onChange?: (value: any) => void;
  onFilterChange?: (value: any) => void;
  onSearch?: (value: any) => void;
  isEnabled?: boolean;
}

const INT32_MAX = 2147483647;
const INT32_MIN = -2147483648;

const INT64_IEEEE754_MAX = 9007199254740991;
const INT64_IEEEE754_MIN = -9007199254740992;

export const FieldMapper: React.FC<FieldMapperProps> = props => {
  const {
    ctField,
    fieldTranslation,
    choices,
    isLoading = false,
    onChange,
    onFilterChange,
    onSearch,
    isEnabled
  } = props;
  const fieldLabel = fieldTranslation ?? ctField.name;

  const handleChange = (event: any) => {
    const value = event.value !== undefined ? event.value : event.target?.value;
    onChange?.(value);
  };

  const fieldDisabled = ctField.readOnly || isLoading || !isEnabled;

  switch (ctField.type) {
    case GEAR_CONTENT_TYPE_FIELD_TYPE.BOOL:
      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={FormBoolRadioGroup}
          label={fieldLabel}
          validator={ctField.required ? requiredValidator : undefined}
          optional={ctField.required ? false : true}
          onChange={handleChange}
          disabled={fieldDisabled}
        />
      );
    case GEAR_CONTENT_TYPE_FIELD_TYPE.DATE:
    case GEAR_CONTENT_TYPE_FIELD_TYPE.DATETIME:
      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={FormDatePicker}
          label={fieldLabel}
          validator={ctField.required ? requiredValidator : undefined}
          optional={ctField.required ? false : true}
          includeTime={ctField.type === GEAR_CONTENT_TYPE_FIELD_TYPE.DATETIME}
          hint={ctField.hint}
          onChange={handleChange}
          disabled={fieldDisabled}
        />
      );
    case GEAR_CONTENT_TYPE_FIELD_TYPE.DECIMAL:
    case GEAR_CONTENT_TYPE_FIELD_TYPE.INT:
    case GEAR_CONTENT_TYPE_FIELD_TYPE.INT64: {
      let max = undefined;
      let min = undefined;
      if (ctField.type === GEAR_CONTENT_TYPE_FIELD_TYPE.INT) {
        max = INT32_MAX;
        min = INT32_MIN;
      } else if (ctField.type === GEAR_CONTENT_TYPE_FIELD_TYPE.INT64) {
        max = INT64_IEEEE754_MAX;
        min = INT64_IEEEE754_MIN;
      }

      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={FormNumberInput}
          label={fieldLabel}
          validator={buildFieldValidators(ctField)}
          optional={ctField.required ? false : true}
          isInteger={ctField.type !== GEAR_CONTENT_TYPE_FIELD_TYPE.DECIMAL}
          hint={ctField.hint}
          min={ctField.min ?? min}
          max={ctField.max ?? max}
          onChange={handleChange}
          disabled={fieldDisabled}
          isLoading={isLoading}
        />
      );
    }
    case GEAR_CONTENT_TYPE_FIELD_TYPE.CHOICE:
      if (ctField.searchable && ctField.usesExtDictionary) {
        return (
          <Field
            key={ctField.name}
            name={ctField.name}
            component={FormComboBox}
            choices={choices}
            label={fieldLabel}
            defaultValue={ctField.defaultValue}
            validator={ctField.required ? requiredValidator : undefined}
            optional={ctField.required ? false : true}
            onFilterChange={(value: string) => onFilterChange?.(value)}
            onSelectionChange={(value: any) => onChange?.(value)}
            disabled={fieldDisabled}
            isLoading={isLoading}
          />
        );
      }
      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={FormDropDownList}
          data={choices}
          label={fieldLabel}
          defaultValue={ctField.defaultValue}
          validator={ctField.required ? requiredValidator : undefined}
          optional={ctField.required ? false : true}
          onChange={handleChange}
          disabled={fieldDisabled}
          isLoading={isLoading}
        />
      );
    case GEAR_CONTENT_TYPE_FIELD_TYPE.MULTI_CHOICE:
      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={FormMultiSelect}
          choices={choices}
          defaultValue={ctField.defaultValue}
          label={fieldLabel}
          validator={ctField.required ? requiredValidator : undefined}
          optional={ctField.required ? false : true}
          onFilterChange={
            ctField.searchable && ctField.usesExtDictionary ? (value: string) => onFilterChange?.(value) : undefined
          }
          onSelectionChange={(value: any) => onChange?.(value)}
          disabled={fieldDisabled}
          isLoading={isLoading}
        />
      );
    default:
      if (ctField.searchable && ctField.formFiller && ctField.usesExtDictionary) {
        return (
          <Field
            key={ctField.name}
            name={ctField.name}
            component={FormSearchableInput}
            label={fieldLabel}
            validator={buildFieldValidators(ctField)}
            optional={ctField.required ? false : true}
            hint={ctField.hint}
            min={ctField.min}
            max={ctField.max}
            searchResults={choices}
            isLoading={isLoading}
            onSearch={onSearch}
            disabled={fieldDisabled}
          />
        );
      }
      return (
        <Field
          key={ctField.name}
          name={ctField.name}
          component={ctField.mask ? FormMaskedInput : FormInput}
          label={fieldLabel}
          validator={buildFieldValidators(ctField)}
          optional={ctField.required ? false : true}
          hint={ctField.hint}
          min={ctField.min}
          max={ctField.max}
          mask={ctField.mask}
          onChange={handleChange}
          disabled={fieldDisabled}
          isLoading={isLoading}
        />
      );
  }
};
